// FacturaScanner.jsx
// Estrategia: jsQR primero (local, gratis) → Claude Vision como fallback
// Debug panel integrado para ver logs en tiempo real

import { useState, useRef, useCallback, useEffect } from 'react'
import { supabase } from '../lib/supabaseClient'
import { encryptField } from '../lib/secureFields'
import { useLanguage } from '../hooks/useLanguage'

// ─── Utilidades ──────────────────────────────────────────────────────────────

function isMobileDevice() {
  return (
    typeof window !== 'undefined' &&
    (navigator.maxTouchPoints > 0 ||
      /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(navigator.userAgent))
  )
}

function formatCOP(raw) {
  const digits = String(raw).replace(/\D/g, '')
  if (!digits) return ''
  return parseInt(digits, 10).toLocaleString('es-CO')
}

function parseCOP(formatted) {
  return parseFloat(String(formatted).replace(/\./g, '').replace(',', '.')) || 0
}

function getTodayDate() {
  const today = new Date()
  const y = today.getFullYear()
  const m = String(today.getMonth() + 1).padStart(2, '0')
  const d = String(today.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ─── Claude Vision: extrae datos leyendo el TEXTO de la factura ──────────────

async function extraerConClaudeVision(base64, mediaType, addLog) {
  const SUPABASE_URL = 'https://lrwjahmdpflelhargtet.supabase.co'
  const SUPABASE_ANON_KEY = 'sb_publishable_6ZAnXdmA-gmWOMjJkOZfOg_aDE_WmkZ'
  const url = `${SUPABASE_URL}/functions/v1/extraer-factura`
  addLog(`🔗 URL Edge Function: ${url}`)
  addLog('🤖 Iniciando Claude Vision (Edge Function)...')

  const res = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    },
    body: JSON.stringify({ base64, mediaType })
  })

  addLog(`📡 Edge Function HTTP: ${res.status}`)
  const txt = await res.text()
  addLog(`📥 Respuesta: ${txt.substring(0, 300)}`)

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}: ${txt.substring(0, 100)}`)
  }

  const datos = JSON.parse(txt)
  if (datos.error) throw new Error(datos.error)

  addLog(`✅ Claude Vision OK → total: ${datos.total}, fecha: ${datos.fecha}`)
  return datos
}

// ─── Categorías ──────────────────────────────────────────────────────────────

const CATEGORIAS_GRUPOS = [
  { label: '🏠 Gastos fijos', options: ['Arriendo', 'Administración'] },
  { label: '⚡ Servicios públicos', options: ['Agua', 'Luz', 'Gas', 'Internet'] },
  { label: '🍽️ Gastos básicos', options: ['Mercado', 'Alimentación', 'Transporte'] },
  { label: '💳 Deudas y obligaciones', options: ['Tarjetas de crédito', 'Créditos'] },
  { label: '🏥 Salud y protección', options: ['EPS', 'Medicina prepagada'] },
  { label: '📱 Gastos personales', options: ['Celular', 'Streaming', 'Ropa'] },
  { label: '🎉 Ocio y estilo de vida', options: ['Salidas', 'Viajes'] },
  { label: '➕ Personalizado', options: ['Otro'] },
]

// ─── Componente principal ─────────────────────────────────────────────────────

export default function FacturaScanner({ userId, onGastoRegistrado }) {
  const { t } = useLanguage()
  const inputCameraRef = useRef(null)
  const inputGalleryRef = useRef(null)
  const esMobil = isMobileDevice()

  // Conexión
  const [isOnline, setIsOnline] = useState(navigator.onLine)

  useEffect(() => {
    function handleOnline() { setIsOnline(true) }
    function handleOffline() { setIsOnline(false) }
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)
    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  // Estado principal
  const [estado, setEstado] = useState('idle')
  // idle | procesando | formulario | guardando | exito | guia

  // Datos del formulario
  const [montoDisplay, setMontoDisplay] = useState('')
  const [categoria, setCategoria] = useState('')
  const [categoriaCustom, setCategoriaCustom] = useState('')
  const [fecha, setFecha] = useState(getTodayDate())
  const [descripcion, setDescripcion] = useState('')
  const [errorMsg, setErrorMsg] = useState('')
  const [fuenteDatos, setFuenteDatos] = useState(null) // 'qr' | 'vision' | 'manual'

  // UI
  const [showMenu, setShowMenu] = useState(false)
  const [pressed, setPressed] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  // Debug panel
  const [debugLogs, setDebugLogs] = useState([])
  const [showDebug, setShowDebug] = useState(false)
  const debugRef = useRef(null)

  const addLog = useCallback((msg) => {
    const ts = new Date().toLocaleTimeString('es-CO', { hour12: false })
    setDebugLogs(prev => [...prev, `[${ts}] ${msg}`])
  }, [])

  useEffect(() => {
    if (debugRef.current) {
      debugRef.current.scrollTop = debugRef.current.scrollHeight
    }
  }, [debugLogs])

  // ─── Procesar imagen (pipeline completo) ────────────────────────────────────

  const procesarImagen = useCallback(async (file) => {
    if (!file) return

    if (!isOnline) {
      setShowMenu(false)
      setErrorMsg(t('factura_sin_internet_error'))
      setEstado('formulario')
      setFuenteDatos('manual')
      return
    }

    setShowMenu(false)
    setEstado('procesando')
    setErrorMsg('')
    setDebugLogs([])
    setFuenteDatos(null)

    addLog(`📎 Archivo: ${file.name} (${(file.size / 1024).toFixed(1)} KB, ${file.type})`)

    try {
      // 1. Convertir a base64 y cargar como imagen
      const { base64, mediaType, objectUrl } = await new Promise((res, rej) => {
        const reader = new FileReader()
        reader.onload = () => {
          const dataUrl = reader.result
          const parts = dataUrl.split(',')
          const mt = parts[0].match(/data:([^;]+)/)?.[1] || 'image/jpeg'
          const b64 = parts[1]
          const blob = new Blob([file], { type: file.type })
          const url = URL.createObjectURL(blob)
          res({ base64: b64, mediaType: mt, objectUrl: url })
        }
        reader.onerror = () => rej(new Error('Error al leer el archivo'))
        reader.readAsDataURL(file)
      })

      addLog(`🖼️ Imagen convertida a base64 (${mediaType})`)

      // 2. Liberar objectUrl ya no es necesario para jsQR
      URL.revokeObjectURL(objectUrl)

      // 3. Claude Vision lee el texto de la factura
      let datosExtraidos = null
      addLog('🤖 Enviando imagen a Claude Vision...')

      if (true) {
        try {
          const vision = await extraerConClaudeVision(base64, mediaType, addLog)
          if (vision.total && vision.total > 0) {
            datosExtraidos = { ...vision, fuente: 'vision' }
            setFuenteDatos('vision')
          } else {
            addLog('⚠️ Claude Vision no encontró total → modo manual')
            setFuenteDatos('manual')
          }
        } catch (err) {
          addLog(`❌ Error Claude Vision: ${err.message}`)
          setFuenteDatos('manual')
        }
      }

      // 5. Poblar formulario
      if (datosExtraidos?.total) {
        setMontoDisplay(formatCOP(String(Math.round(datosExtraidos.total))))
        addLog(`✅ Monto seteado: ${formatCOP(String(Math.round(datosExtraidos.total)))}`)
      }
      if (datosExtraidos?.fecha) {
        setFecha(datosExtraidos.fecha)
      }
      if (datosExtraidos?.descripcion) {
        setDescripcion(datosExtraidos.descripcion)
      }

      setEstado('formulario')

    } catch (err) {
      addLog(`💥 Error general: ${err.message}`)
      setErrorMsg(`${t('factura_error_procesar_imagen')} ${err.message}`)
      setFuenteDatos('manual')
      setEstado('formulario')
    }
  }, [addLog, isOnline, t])

  // ─── Guardar gasto ──────────────────────────────────────────────────────────

  const registrarGasto = async () => {
    if (!isOnline) { setErrorMsg(t('factura_sin_internet_error')); return }

    const montoNum = parseCOP(montoDisplay)
    if (!montoNum || montoNum <= 0) { setErrorMsg(t('factura_monto_invalido')); return }
    if (!userId) { setErrorMsg(t('factura_usuario_no_identificado')); return }

    const categoriaFinal = categoria === 'Otro'
      ? (categoriaCustom.trim() || 'Otro')
      : categoria

    if (!categoriaFinal) { setErrorMsg(t('factura_selecciona_categoria_error')); return }

    setEstado('guardando')
    setErrorMsg('')

    try {
      addLog(`💾 Guardando: $${montoNum} | ${categoriaFinal} | ${fecha}`)

      const encryptedCategory = await encryptField(categoriaFinal)
      const encryptedDescription = await encryptField(descripcion || categoriaFinal)

      const { error } = await supabase.from('transactions').insert({
        user_id: userId,
        type: 'expense',
        amount: montoNum,
        date: fecha,
        category: encryptedCategory,
        description: encryptedDescription,
      })

      if (error) throw error

      addLog('✅ Gasto guardado en Supabase')
      onGastoRegistrado?.({ amount: montoNum, category: categoriaFinal, date: fecha })
      setEstado('exito')
      setTimeout(() => resetear(), 2500)

    } catch (err) {
      addLog(`❌ Error al guardar: ${err.message}`)
      setErrorMsg(`${t('factura_error_guardar')} ${err.message}`)
      setEstado('formulario')
    }

  }

  // ─── Reset ──────────────────────────────────────────────────────────────────

  const resetear = () => {
    setEstado('idle')
    setMontoDisplay('')
    setCategoria('')
    setCategoriaCustom('')
    setFecha(getTodayDate())
    setDescripcion('')
    setErrorMsg('')
    setFuenteDatos(null)
    setShowMenu(false)
    if (inputCameraRef.current) inputCameraRef.current.value = ''
    if (inputGalleryRef.current) inputGalleryRef.current.value = ''
  }

  // ─── Estilos reutilizables ──────────────────────────────────────────────────

  const inputStyle = {
    width: '100%',
    background: 'var(--color-surface)',
    border: '1px solid rgba(255, 0, 0, 0.1)',
    borderRadius: 12,
    padding: '11px 14px',
    fontSize: 14,
    color: 'white',
    outline: 'none',
    boxSizing: 'border-box',
    fontFamily: 'inherit',
  }

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: '#94a3b8',
    marginBottom: 6,
    display: 'block',
    letterSpacing: 0.4,
  }

  const iconColor = '#a5b4fc'

  // ─── Badge de fuente de datos ────────────────────────────────────────────────

  const FuenteBadge = () => {
    if (!fuenteDatos) return null
    const config = {
      qr:     { bg: 'rgba(34,197,94,0.12)',  border: 'rgba(34,197,94,0.3)',   color: '#86efac', icon: '📱', label: t('factura_fuente_qr') },
      vision: { bg: 'rgba(99,102,241,0.12)', border: 'rgba(99,102,241,0.3)',  color: '#a5b4fc', icon: '🤖', label: t('factura_fuente_vision') },
      manual: { bg: 'rgba(251,191,36,0.12)', border: 'rgba(251,191,36,0.3)',  color: '#fde68a', icon: '✏️', label: t('factura_fuente_manual') },
    }[fuenteDatos]

    return (
      <div style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '4px 10px', borderRadius: 20,
        background: config.bg, border: `1px solid ${config.border}`,
        fontSize: 11, fontWeight: 700, color: config.color
      }}>
        {config.icon} {config.label}
      </div>
    )
  }

  // ─── Formulario ─────────────────────────────────────────────────────────────

  const renderFormulario = () => (
    <div>
      {/* Header con badge */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text)', marginBottom: 4 }}>
            {fuenteDatos === 'qr' ? t('factura_qr_detectado') :
            fuenteDatos === 'vision' ? t('factura_analizada') :
            t('factura_ingresa_datos')}
          </div>
          <div style={{ fontSize: 12, color: 'var(--color-text)' }}>
            {fuenteDatos === 'manual'
              ? t('factura_no_pudo_leer')
              : t('factura_verifica_datos')}
          </div>
          <FuenteBadge />
        </div>

        {/* Botón debug */}
        <button
          onClick={() => setShowDebug(p => !p)}
          style={{
            marginTop: 10, padding: '4px 10px', borderRadius: 6,
            background: showDebug ? 'var(--color-surface)' : 'rgba(255,255,255,0.04)',
            border: `1px solid ${showDebug ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.08)'}`,
            color: showDebug ? '#a5b4fc' : '#475569',
            fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'monospace'
          }}
        >
          {showDebug ? '▼' : '▶'} DEBUG ({debugLogs.length} logs)
        </button>
      </div>

      {/* Panel debug */}
      {showDebug && (
        <div
          ref={debugRef}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid rgba(99,102,241,0.3)',
            borderRadius: 10, padding: '10px 12px',
            marginBottom: 14, maxHeight: 180,
            overflowY: 'auto', fontFamily: 'monospace',
            fontSize: 11, lineHeight: 1.6,
            color: '#94a3b8'
          }}
        >
          {debugLogs.length === 0
            ? <span style={{ color: 'var(--color-text)' }}>{t('factura_sin_logs')}</span>
            : debugLogs.map((log, i) => {
                const color =
                  log.includes('✅') || log.includes('🎯') ? '#86efac' :
                  log.includes('❌') || log.includes('💥') ? '#f87171' :
                  log.includes('⚠️') ? '#fde68a' :
                  log.includes('🤖') || log.includes('📡') ? '#a5b4fc' :
                  '#94a3b8'
                return <div key={i} style={{ color }}>{log}</div>
              })
          }
        </div>
      )}

      {/* Error */}
      {errorMsg && (
        <div style={{
          fontSize: 12, color: '#f87171',
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.2)',
          borderRadius: 10, padding: '8px 12px', marginBottom: 12
        }}>
          {errorMsg}
        </div>
      )}

      {/* Campos */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

        {/* Monto */}
        <div>
          <label style={labelStyle}>{t('factura_monto_total')}</label>
          <div style={{ position: 'relative' }}>
            <span style={{
              position: 'absolute', left: 14, top: '50%',
              transform: 'translateY(-50%)',
              color: '#94a3b8', fontSize: 15, fontWeight: 700
            }}>$</span>
            <input
              type="text" inputMode="numeric"
              placeholder="0"
              value={montoDisplay}
              onChange={(e) => setMontoDisplay(formatCOP(e.target.value))}
              style={{ ...inputStyle, paddingLeft: 28, fontSize: 18, fontWeight: 700 }}
            />
          </div>
        </div>

        {/* Categoría */}
        <div>
        <label style={labelStyle}>{t('factura_categoria_label')}</label>
        <select
          value={categoria}
          onChange={(e) => setCategoria(e.target.value)}
          style={{ ...inputStyle, cursor: 'pointer' }}
        >
          <option value="">{t('selecciona_categoria')}</option>
            {CATEGORIAS_GRUPOS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((op) => <option key={op} value={op}>{op}</option>)}
              </optgroup>
            ))}
          </select>
          </div>
        {categoria === 'Otro' && (
          <div>
            <label style={labelStyle}>{t('factura_nombre_categoria')}</label>
            <input
              type="text"
              placeholder={t('factura_categoria_custom_placeholder')}
              value={categoriaCustom}
              onChange={(e) => setCategoriaCustom(e.target.value)}
              style={inputStyle}
            />
          </div>
        )}

        {/* Fecha + Descripción */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <div>
            <label style={labelStyle}>{t('factura_fecha_label')}</label>
            <input
              type="date" value={fecha}
              onChange={(e) => setFecha(e.target.value)}
              style={{ ...inputStyle, colorScheme: 'dark' }}
            />
          </div>
          <div>
            <label style={labelStyle}>{t('factura_desc_label')}</label>
              <input
                type="text"
                placeholder={t('factura_desc_placeholder')}
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                style={inputStyle}
              />
          </div>
        </div>
      </div>

      {/* Botones */}
      <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
        <button
          onClick={registrarGasto}
          disabled={!montoDisplay}
          style={{
            flex: 1, padding: '12px 16px',
            background: montoDisplay ? 'var(--color-surface)' : 'var(--color-surface)',
            border: 'none', borderRadius: 14,
            color: 'white', fontWeight: 700, fontSize: 14,
            cursor: montoDisplay ? 'pointer' : 'not-allowed',
            transition: '0.2s'
          }}
          onMouseEnter={(e) => { if (montoDisplay) e.currentTarget.style.background = '#dc2626' }}
          onMouseLeave={(e) => { if (montoDisplay) e.currentTarget.style.background = '#ef4444' }}
        >
          {t('factura_registrar_gasto')}
        </button>
        <button
          onClick={resetear}
          style={{
            flex: 1, padding: '12px 16px',
            background: 'color: var(--color-surface)',
            border: '1px solid color: var(--color-surface)',
            borderRadius: 14, color: 'var(--color-text)',
            fontWeight: 700, fontSize: 14, cursor: 'pointer',
          }}
        >
          {t('factura_cancelar')}
        </button>
      </div>
    </div>
  )

  // ─── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Inputs ocultos */}
      <input
        ref={inputCameraRef}
        type="file" accept="image/*" capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => procesarImagen(e.target.files?.[0])}
      />
      <input
        ref={inputGalleryRef}
        type="file" accept="image/*"
        style={{ display: 'none' }}
        onChange={(e) => procesarImagen(e.target.files?.[0])}
      />

      {/* ── Botón flotante principal ── */}
      <div style={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
        <button
          onClick={() => setShowMenu(!showMenu)}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onTouchStart={() => setPressed(true)}
          onTouchEnd={() => setPressed(false)}
          aria-label={t('factura_aria_escanear')}
          style={{
            width: 48, height: 48, borderRadius: '50%',
            background: 'linear-gradient(145deg, #1e293b, #0f172a)',
            border: '1.5px solid rgba(99,102,241,0.5)',
            boxShadow: '0 4px 18px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', position: 'relative',
            transform: pressed ? 'scale(0.93)' : 'scale(1)',
            transition: 'all 0.2s cubic-bezier(0.34,1.56,0.64,1)',
            padding: 0, outline: 'none',
          }}
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="3" y="3" width="7" height="7" rx="1" stroke={iconColor} strokeWidth="1.5" fill="none"/>
            <rect x="5" y="5" width="3" height="3" fill={iconColor}/>
            <rect x="14" y="3" width="7" height="7" rx="1" stroke={iconColor} strokeWidth="1.5" fill="none"/>
            <rect x="16" y="5" width="3" height="3" fill={iconColor}/>
            <rect x="3" y="14" width="7" height="7" rx="1" stroke={iconColor} strokeWidth="1.5" fill="none"/>
            <rect x="5" y="16" width="3" height="3" fill={iconColor}/>
            <line x1="14" y1="14" x2="14" y2="14.01" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
            <line x1="17" y1="14" x2="21" y2="14" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="14" y1="17" x2="14" y2="21" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="17" y1="17" x2="17" y2="17.01" stroke={iconColor} strokeWidth="2" strokeLinecap="round"/>
            <line x1="20" y1="17" x2="21" y2="17" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="20" y1="17" x2="20" y2="21" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round"/>
            <line x1="17" y1="20" x2="17" y2="21" stroke={iconColor} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <div style={{
            position: 'absolute', inset: -5, borderRadius: '50%',
            border: '1.5px solid rgba(99,102,241,0.35)',
            animation: 'pulseRing 2.5s ease-out infinite',
            pointerEvents: 'none'
          }} />
          {!isOnline && (
            <div style={{
              position: 'absolute', bottom: -2, right: -2,
              width: 16, height: 16, borderRadius: '50%',
              background: '#ef4444', border: '2px solid var(--color-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 9,
            }}>!</div>
          )}
        </button>

        <div style={{ fontSize: 9, fontWeight: 700, color: '#818cf8', marginTop: 4, letterSpacing: 0.5 }}>
          {t('factura_btn_label')}
        </div>

        {/* Menú desplegable */}
        {showMenu && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            background:'var(--color-surface)', border: '1px solid rgba(99,102,241,0.4)',
            borderRadius: '4px 12px 12px 12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
            zIndex: 9999, overflow: 'hidden', minWidth: 170,
            animation: 'slideDown 0.15s ease',
          }}>
            {!isOnline && (
              <div style={{
                padding: '10px 14px', fontSize: 11, color: '#fde68a',
                background: 'rgba(251,191,36,0.1)',
                borderBottom: '1px solid rgba(255,255,255,0.06)',
                lineHeight: 1.4,
              }}>
                📡 {t('factura_menu_sin_internet')}
              </div>
            )}
            {esMobil && (
            <button
              onClick={() => { setShowMenu(false); setEstado('guia') }}
                style={{
                  width: '100%', padding: '12px 16px',
                  background: 'none', border: 'none',
                  color: '#a5b4fc', fontSize: 13, fontWeight: 600,
                  cursor: 'pointer', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  borderBottom: '1px solid rgba(255,255,255,0.06)'
                }}
              >
                {t('factura_tomar_foto')}
              </button>
            )}
            <button
              onClick={() => { setShowMenu(false); inputGalleryRef.current?.click() }}
              style={{
                width: '100%', padding: '12px 16px',
                background: 'none', border: 'none',
                color: '#a5b4fc', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10,
                borderBottom: '1px solid rgba(255,255,255,0.06)'
              }}
            >
              {t('factura_elegir_galeria')}
            </button>
            <button
              onClick={() => { setShowMenu(false); setFuenteDatos('manual'); setEstado('formulario') }}
              style={{
                width: '100%', padding: '12px 16px',
                background: 'none', border: 'none',
                color: '#64748b', fontSize: 13, fontWeight: 600,
                cursor: 'pointer', textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 10
              }}
            >
              {t('factura_ingresar_manual')}
            </button>
          </div>
        )}

        {/* Tooltip desktop */}
        {showTooltip && !showMenu && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 8px)', right: 0,
            background: '#1e293b', border: '1px solid rgba(99,102,241,0.4)',
            borderRadius: '4px 12px 12px 12px', padding: '10px 12px',
            fontSize: 12, color: '#a5b4fc',
            boxShadow: '0 4px 16px rgba(0,0,0,0.4)',
            zIndex: 9999, pointerEvents: 'none',
            lineHeight: 1.5, whiteSpace: 'nowrap',
          }}>
            {t('Escanear_facturaqr')}
          </div>
        )}
      </div>

      {/* ── Modal principal ── */}
      {estado !== 'idle' && (
        <div
          onClick={['procesando', 'guardando', 'exito', 'guia'].includes(estado) ? undefined : resetear}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            zIndex: 99999,
            display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
            padding: '0 12px 28px',
            animation: 'fadeInBg 0.2s ease'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--color-bg)',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 24, padding: 20,
              width: '100%', maxWidth: 440,
              maxHeight: '90vh', overflowY: 'auto',
              animation: 'slideUp 0.3s cubic-bezier(0.34,1.56,0.64,1)'
            }}
          >

            {/* Estado: guia */}
            {estado === 'guia' && (
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>📄</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#ffffff', marginBottom: 8, textShadow: '0 1px 4px rgba(0,0,0,0.5)' }}>
                  {t('factura_guia_titulo')}
                </div>
                <div style={{
                  fontSize: 13,
                  marginBottom: 20, lineHeight: 1.7,
                  textAlign: 'left',
                  background: 'rgba(0,0,0,0.55)',
                  backdropFilter: 'blur(8px)',
                  borderRadius: 14,
                  padding: '14px 16px',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}>
                {!isOnline && (
                  <div style={{ marginBottom: 10, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                    <span>📡</span>
                    <span style={{ color: '#fde68a', fontWeight: 700 }}>{t('factura_guia_tip_internet')}</span>
                  </div>
                )}
                <div style={{ marginBottom: 10, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span>✅</span>
                  <span style={{ color: 'rgba(255,255,255,0.9)' }}>{t('factura_guia_tip_1')}</span>
                </div>
                <div style={{ marginBottom: 10, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span>✅</span>
                  <span style={{ color: 'rgba(255,255,255,0.9)' }}>{t('factura_guia_tip_2')}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <span>❌</span>
                  <span style={{ color: 'rgba(255,255,255,0.9)' }}>{t('factura_guia_tip_3')}</span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => isOnline && inputCameraRef.current?.click()}
                  disabled={!isOnline}
                  style={{
                    flex: 1, padding: '12px 16px',
                    background: isOnline
                      ? 'radial-gradient(ellipse at 60% 30%, var(--color-primary-lt), transparent 80%), var(--color-surface)'
                      : 'var(--color-surface)',
                    border: `1px solid ${isOnline ? 'var(--color-primary)' : 'var(--color-border)'}`,
                    borderRadius: 14,
                    color: isOnline ? 'var(--color-primary)' : 'var(--color-muted)',
                    fontWeight: 700, fontSize: 14,
                    cursor: isOnline ? 'pointer' : 'not-allowed',
                    opacity: isOnline ? 1 : 0.6,
                    backdropFilter: 'blur(8px)',
                    boxShadow: isOnline ? 'var(--shadow-btn)' : 'none',
                    transition: 'var(--transition)',
                  }}
                >
                  📸 {t('factura_guia_boton_foto')}
                </button>
                <button
                  onClick={resetear}
                  style={{
                    flex: 1, padding: '12px 16px',
                    background: 'radial-gradient(ellipse at 60% 30%, rgba(255,255,255,0.04), transparent 80%), var(--color-surface)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 14,
                    color: 'var(--color-muted)',
                    fontWeight: 700, fontSize: 14, cursor: 'pointer',
                    backdropFilter: 'blur(8px)',
                    transition: 'var(--transition)',
                  }}
                >
                  ✕ {t('factura_cancelar')}
                </button>
              </div>
            </div>
          )}

            {/* Estado: procesando */}
            {estado === 'procesando' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{
                  width: 48, height: 48, margin: '0 auto 16px',
                  border: '3px solid rgba(99,102,241,0.2)',
                  borderTop: '3px solid #818cf8',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <div style={{ fontSize: 15, fontWeight: 700, color: 'white', marginBottom: 6 }}>
                  {t('factura_analizando_titulo')}
                </div>
                <div style={{ fontSize: 13, color: '#64748b', marginBottom: 16 }}>
                  {t('factura_analizando_desc')}
                </div>

                {/* Debug en vivo durante procesamiento */}
                {debugLogs.length > 0 && (
                  <div
                    ref={debugRef}
                    style={{
                      background: 'var(--color-surface)',
                      border: '1px solid rgba(99,102,241,0.2)',
                      borderRadius: 10, padding: '8px 12px',
                      maxHeight: 140, overflowY: 'auto',
                      fontFamily: 'monospace', fontSize: 11,
                      lineHeight: 1.6, textAlign: 'left'
                    }}
                  >
                    {debugLogs.map((log, i) => {
                      const color =
                        log.includes('✅') || log.includes('🎯') ? '#86efac' :
                        log.includes('❌') || log.includes('💥') ? '#f87171' :
                        log.includes('⚠️') ? '#fde68a' :
                        log.includes('🤖') ? '#a5b4fc' : '#64748b'
                      return <div key={i} style={{ color }}>{log}</div>
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Estado: formulario */}
            {estado === 'formulario' && renderFormulario()}

            {/* Estado: guardando */}
            {estado === 'guardando' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{
                  width: 48, height: 48, margin: '0 auto 16px',
                  border: '3px solid rgba(239,68,68,0.2)',
                  borderTop: '3px solid #446cef',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <div style={{ fontSize: 15, fontWeight: 700, color: 'white' }}>
                  {t('factura_guardando_titulo')}
                </div>
              </div>
            )}

            {/* Estado: exito */}
            {estado === 'exito' && (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <div style={{ fontSize: 52, marginBottom: 12 }}>✅</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: '#86efac', marginBottom: 6 }}>
                  {t('factura_exito_titulo')}
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  $ {montoDisplay} · {categoria === 'Otro' ? categoriaCustom : categoria}
                </div>
                <div style={{ fontSize: 11, color: '#475569', marginTop: 6 }}>
                  {t('factura_exito_fuente')} {fuenteDatos === 'qr' ? `📱 ${t('factura_fuente_qr')}` : fuenteDatos === 'vision' ? `🤖 ${t('factura_fuente_vision')}` : `✏️ ${t('factura_fuente_manual')}`}
                </div>
              </div>
            )}

          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes pulseRing { 0%{opacity:0.6;transform:scale(1)} 100%{opacity:0;transform:scale(1.65)} }
        @keyframes fadeInBg { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{opacity:0;transform:translateY(48px)} to{opacity:1;transform:translateY(0)} }
        @keyframes slideDown { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
        input::placeholder { color: #475569 !important; }
        select option { background: color: 'var(--color-surface)' }
      `}</style>
    </>
  )
}